import { OrderServicePublicClient } from '@jbhi-fi/apis/src/orderService';
import React, { useRef, useState } from 'react';
import { useEffect } from 'react';
import { styles } from './RiskifiedOtp.css';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';
import ErrorIcon from '@jbhi-fi/lanyard-ui/components/Icons/Error';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import TickIcon from '@jbhi-fi/lanyard-ui/components/Icons/Tick';
import { Tracker } from '@jbhi-fi/components/src/Tracking';
import { Legals } from '@jbhi-fi/components/src/FooterLayout/Legals';
import { HeaderJbLogo } from '@jbhi-fi/components/src/HeaderLayout/components/HeaderJbLogo/HeaderJbLogo';
import { getFooterNavData } from '../footer/utils/getFooterNavData';

const orderServicePublicClient = new OrderServicePublicClient({
  orderServicePublicUrl: process.env.ORDER_SERVICE_PUBLIC_URL || '',
  orderServicePublicApiKey: process.env.ORDER_SERVICE_PUBLIC_API_KEY || '',
  countryCode: window.Jbhifi.storeLocation,
});

type OtpState = 'idle' | 'success' | 'error';

export const RiskifiedOtp = () => {
  const [otpState, setOtpState] = useState<OtpState>('idle');
  const [hasIframeLoaded, setHasIframeLoaded] = useState(false);
  const widgetContainerRef = useRef(null);
  const { footerLegals } = getFooterNavData() || {};
  const legalsProps = footerLegals?.legalsLinks?.length ? footerLegals : undefined;

  useEffect(() => {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeName === 'IFRAME') {
            node.addEventListener('load', () => {
              setHasIframeLoaded(true);
            });
          }
        });
      });
    });

    if (widgetContainerRef.current) {
      observer.observe(widgetContainerRef.current, { childList: true });
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const initiateOtp = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const orderId = urlParams.get('orderId');
      if (!orderId) {
        setOtpState('error');
        return;
      }
      const { data, error } = await orderServicePublicClient.initiateOtp({ orderId });
      if (error) {
        console.error('Error initiating OTP', error);
        setOtpState('error');
        return;
      }

      const widgetToken = data?.widgetToken;
      if (!widgetToken) {
        setOtpState('error');
        return;
      }

      window.Riskified?.renderOTPWidget(
        widgetToken,
        // Success callback
        async (challengeToken: string) => {
          const { isSuccess, error } = await orderServicePublicClient.verifyOtp({
            orderId,
            challengeToken,
          });
          if (isSuccess) {
            setOtpState('success');
          } else {
            console.error('Error verifying OTP', error);
            document.querySelector('#otp-widget iframe')?.remove();
            setOtpState('error');
          }
        },
        // Timeout callback
        () => {
          console.error('OTP widget timed out');
          document.querySelector('iframe')?.remove();
          setOtpState('error');
        }
      );
    };
    initiateOtp();
  }, []);

  useEffect(() => {
    if (otpState === 'success' || otpState === 'error') {
      Tracker.trackCustomEvent({
        event: `verification_${otpState === 'success' ? 'success' : 'failed'}`,
      });
    }
  }, [otpState]);

  return (
    <>
      <HeaderJbLogo />
      <div className={styles.container}>
        {otpState === 'idle' ? (
          <>
            <div ref={widgetContainerRef} className={styles.widgetContainer} id="otp-widget"></div>
            {!hasIframeLoaded ? <Spinner className={styles.spinner} /> : null}
          </>
        ) : (
          <div className={styles.messageContainer}>
            {otpState === 'error' ? (
              <>
                <div className={styles.icon}>
                  <ErrorIcon color={palette.error.main} />
                </div>
                <h2 className={styles.heading}>Verification unavailable</h2>
                <p className={styles.text}>
                  Sorry, we ran into an issue and are unable to complete your identity verification right now.
                  <br />
                  <br />
                  Please contact our verification team at{' '}
                  <a href="mailto:verificationteam@jbhifi.com.au">verificationteam@jbhifi.com.au</a> for assistance.
                </p>
              </>
            ) : null}
            {otpState === 'success' ? (
              <>
                <div className={styles.icon}>
                  <TickIcon color={palette.success.main} />
                </div>
                <h2 className={styles.heading}>Great! You’re all done.</h2>
                <p className={styles.text}>We’ll send a confirmation email with your order details shortly.</p>
              </>
            ) : null}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Legals {...legalsProps} />
      </div>
    </>
  );
};
